import {FollowProfile} from "@/src/features/following/components";
import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import { PostSkeleton } from "@/src/components/posts/loading-skeletons/post-skeleton";
import PostCard from "@/src/components/posts/post-card";
import CreatePostModal from "@/src/components/posts/post-creation-modal";
import InfoRow from "@/src/components/profile/info-row";
import {
  ProfileInfoSkeleton,
  ProfileSkeleton,
} from "@/src/components/profile/profile-loading-skeleton";
import ProfilePhotoGrid from "@/src/components/profile/profile-photo-grid";
import ProfileVideoGrid from "@/src/components/profile/videos/profile-video-grid";
import AttachmentViewer from "@/src/components/ui-kit/attachment-viewer";
import ScButton from "@/src/components/ui-kit/button";
import ScTabs from "@/src/components/ui-kit/tabs";
import UserAvatar from "@/src/components/ui-kit/user-avatar";
import { usePosts } from "@/src/hooks/features/posts/use-posts";
import { useAuth } from "@/src/contexts/auth.context";
import {
  ProfileTab,
  useProfileScreen,
} from "@/src/hooks/features/profile/use-profile-screen";
import { useResponsiveGap } from "@/src/hooks/use-responsive-gap";
import { useTheme } from "@/src/styles/theme-provider";
import {
  ArrowDown2,
  ArrowLeft,
  ArrowUp2,
  DocumentText1,
  Edit,
} from "iconsax-react-nativejs";
import { useRouter, useLocalSearchParams } from "expo-router";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  Text,
  View,
} from "react-native";

export default function ProfileScreen() {
  const { t } = useTranslation();
  const { currentOrgName } = useAuth();
  const { themeVars, varStyle } = useTheme();
  const router = useRouter();
  const {id:profileId}=useLocalSearchParams<{id:string}>();
  const gap = useResponsiveGap();

  const {
    isOwnProfile,
    canAccessEdit,
    activeTab,
    setActiveTab,
    infoExpanded,
    setInfoExpanded,
    blocksUser,
    userProfile,
    isProfileLoading,
    savedPostsData,
    isSavedPostsLoading,
    isSavedPostsError,
    refetchSavedPosts,
    displayName,
    personalInfo,
    workInfo,
    visibleTabs,
    handleNavigateToEdit,
    userPosts,
    userPostsLoading,
    userPostsError,
    userPostsRefetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    userPhotos,
    userDocuments,
    userVideos,
  } = useProfileScreen();

  const {
    createPost,
    handleSubmit,
    editPost,
    setModalOpen,
    modalOpen,
    setEditPost,
    handleDeletePress,
    confirmDeleteOpen,
    handleDeleteConfirm,
    setConfirmDeleteOpen,
    setDeletePostId
  } = usePosts(false);

  const ProfileInfoContent = () => {
    if (isProfileLoading) return <ProfileInfoSkeleton />;
    return (
      <>
        {/* Unconfigured rows are filtered out upstream, so a section can come back
            empty — render its heading only when it has something under it. */}
        {personalInfo.length > 0 && (
          <View className="gap-4">
            <Text className="font-medium text-onyx">
              {t("LABEL_PERSONAL_DETAILS")}
            </Text>
            {personalInfo.map((infoItem) => (
              <InfoRow
                key={infoItem.label}
                label={t(infoItem.label)}
                value={infoItem.value}
                iconName={infoItem.iconName as any}
                isMultiLine={infoItem.isMultiLine}
              />
            ))}
          </View>
        )}
        {workInfo.length > 0 && (
          <View className="gap-4 mt-6">
            <Text className="font-medium text-onyx">
              {t("LABEL_WORK_DETAILS")}
            </Text>
            {workInfo.map((infoItem) => (
              <InfoRow
                key={infoItem.label}
                label={t(infoItem.label)}
                value={infoItem.value}
                iconName={infoItem.iconName as any}
                isMultiLine={infoItem.isMultiLine}
              />
            ))}
          </View>
        )}
      </>
    );
  };

  return (
    <View
      style={varStyle}
      className={`flex-row w-full bg-obsidian flex-1 ${gap}`}
    >
      <ScrollView
        className="w-full md:w-[68%]"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 16 }}
      >
        <View className="bg-chalk p-3 rounded-xl border border-ash-3 flex-row items-center justify-between">
          <Pressable
            onPress={() => router.back()}
            className="flex-row items-center gap-1 px-2 py-1 hover:bg-ash-3 hover:rounded-lg"
          >
            <ArrowLeft size={18} color={themeVars["--color-ash-1"] as string} />
            <Text className="font-medium text-onyx">{t("BTN_BACK")}</Text>
          </Pressable>
        </View>
        {isProfileLoading ? (
          <ProfileSkeleton />
        ) : (
          <View className="bg-chalk overflow-hidden md:rounded-[16px] md:border md:border-ash-3">
            {userProfile?.backgroundImageUrl ? (
              <View className="h-[150px] md:h-[300px]">
                <Image
                  source={{ uri: userProfile.backgroundImageUrl }}
                  style={{ width: "100%", height: "100%" }}
                  resizeMode="cover"
                />
              </View>
            ) : (
              <View className="h-[110px] md:h-[140px] bg-ash" />
            )}

            <View className="p-4 gap-2">
              <View className="relative -mt-16 w-[100px] h-[100px]">
                <View className="w-[100px] h-[100px] rounded-full bg-ash border-4 border-chalk overflow-hidden items-center justify-center">
                  {blocksUser?.profileImageUrl ? (
                    <Image
                      source={{ uri: blocksUser.profileImageUrl }}
                      style={{ width: 100, height: 100 }}
                      resizeMode="cover"
                    />
                  ) : (
                    <UserAvatar
                      firstName={blocksUser?.firstName}
                      lastName={blocksUser?.lastName}
                      size={100}
                    />
                  )}
                </View>
                {canAccessEdit && (
                  <Pressable
                    onPress={handleNavigateToEdit}
                    hitSlop={8}
                    className="md:hidden absolute bottom-0 right-0 bg-primary rounded-full p-1.5 border-2 border-chalk active:opacity-80 hover:opacity-90"
                  >
                    <Edit size={14} color="white" />
                  </Pressable>
                )}
              </View>

              <Text
                className="text-lg font-semibold text-onyx flex-1"
                style={{ flexShrink: 1, wordBreak: "break-word" } as any}
              >
                {displayName ?? t("LABEL_UNKNOWN_USER")}
              </Text>

              <FollowProfile authorId={Array.isArray(profileId)?profileId[0]:profileId??""} own={isOwnProfile}/>
              {userProfile?.description && (
                <Text className="text-sm text-slate">
                  {userProfile.description}
                </Text>
              )}

              <View className="flex-row justify-start gap-2">
                <Text className="text-sm text-ash-1">
                  {t("LABEL_MEMBER_SINCE")}{" "}
                  {blocksUser?.createdDate?.split("T")[0] ??
                    t("LABEL_NOT_AVAILABLE")}
                </Text>
                {currentOrgName && (
                  <Text className="text-sm text-slate font-bold">
                    {t("LABEL_WORKS_AT")}
                    {currentOrgName}
                  </Text>
                )}
              </View>
            </View>

            {/* MOBILE ACCORDION */}
            <View className="md:hidden border-t border-ash-3">
              <Pressable
                onPress={() => setInfoExpanded((prev) => !prev)}
                className="flex-row justify-between items-center px-4 py-3 hover:bg-ash-3 hover:rounded-lg"
              >
                <Text className="font-semibold text-base text-onyx">
                  {t("LABEL_PROFILE_INFORMATION")}
                </Text>
                <View className="flex-row items-center gap-3">
                  {canAccessEdit && (
                    <Pressable onPress={handleNavigateToEdit} hitSlop={8} className="p-1.5 rounded-full hover:bg-ash-3">
                      <Edit size={16} color={themeVars["--color-ash-2"]} />
                    </Pressable>
                  )}
                  {infoExpanded ? (
                    <ArrowUp2 size={16} color={themeVars["--color-ash-2"]} />
                  ) : (
                    <ArrowDown2 size={16} color={themeVars["--color-ash-2"]} />
                  )}
                </View>
              </Pressable>
              {infoExpanded && (
                <View className="px-4 pb-4">
                  <ProfileInfoContent />
                </View>
              )}
            </View>

            <View className="px-4 py-3 border-t border-ash-3">
              <ScTabs
                tabs={visibleTabs}
                active={activeTab}
                onChange={(tab) => setActiveTab(tab as ProfileTab)}
              />
            </View>
          </View>
        )}

        {activeTab === "LABEL_POSTS" && (
          <View className="gap-4">
            {userPostsLoading && [1, 2, 3].map((i) => <PostSkeleton key={i} />)}
            {userPostsError && (
              <View className="items-center py-6 gap-2">
                <Text className="text-slate text-sm">
                  {t("ERROR_LOADING_POST")}
                </Text>
                <ScButton
                  mode="outlined"
                  text="BTN_RETRY"
                  onPress={() => userPostsRefetch()}
                />
              </View>
            )}
            {!userPostsLoading && !userPostsError && (
              <>
                {userPosts.length ? (
                  <>
                    {userPosts.map((post) => (
                      <PostCard
                        key={post.ItemId}
                        post={post}
                        onEdit={(post) => {
                          setEditPost(post);
                          setModalOpen(true);
                        }}
                        onDelete={(post) => {
                          setDeletePostId(post.ItemId);
                          setConfirmDeleteOpen(true);
                        }}
                      />
                    ))}
                    {hasNextPage && (
                      <Pressable
                        onPress={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        className="items-center py-3 hover:bg-ash-3 hover:rounded-lg"
                      >
                        {isFetchingNextPage ? (
                          <ActivityIndicator size="small" />
                        ) : (
                          <Text className="text-sm text-primary underline">
                            {t("LABEL_LOAD_MORE", {
                              defaultValue: "Load more",
                            })}
                          </Text>
                        )}
                      </Pressable>
                    )}
                  </>
                ) : (
                  <View className="items-center py-6">
                    <Text className="text-slate text-sm mb-4">
                      {t("LABEL_EMPTY_POST")}
                    </Text>
                    <Image
                      source={require("@/src/assets/images/empty.png")}
                      style={{ width: 300, height: 300 }}
                      resizeMode="contain"
                    />
                  </View>
                )}
              </>
            )}
          </View>
        )}

        {activeTab === "LABEL_SAVED" && isOwnProfile && (
          <View className="gap-4">
            {isSavedPostsLoading &&
              [1, 2, 3].map((skelKey) => <PostSkeleton key={skelKey} />)}
            {isSavedPostsError && (
              <View className="items-center py-6 gap-2">
                <Text className="text-slate text-sm">
                  {t("ERROR_LOADING_POST")}
                </Text>
                <ScButton
                  mode="outlined"
                  text="BTN_RETRY"
                  onPress={refetchSavedPosts}
                />
              </View>
            )}
            {!isSavedPostsLoading && !isSavedPostsError && (
              <>
                {savedPostsData?.length ? (
                  savedPostsData.map((post) => (
                    <PostCard
                      key={post.ItemId}
                      post={post}
                        onEdit={(post) => {
                          setEditPost(post);
                          setModalOpen(true);
                        }}
                        onDelete={(post) => {
                          setDeletePostId(post.ItemId);
                          setConfirmDeleteOpen(true);
                        }}
                    />
                  ))
                ) : (
                  <View className="items-center py-6">
                    <Text className="text-slate text-sm mb-4">
                      {t("LABEL_EMPTY_POST")}
                    </Text>
                    <Image
                      source={require("@/src/assets/images/empty.png")}
                      style={{ width: 300, height: 300 }}
                      resizeMode="contain"
                    />
                  </View>
                )}
              </>
            )}
          </View>
        )}

        {activeTab === "LABEL_PHOTOS" && (
          <View className="bg-chalk md:rounded-[16px] md:border md:border-ash-3">
            <ProfilePhotoGrid
              photos={userPhotos}
              isLoading={userPostsLoading}
              isError={userPostsError}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={() => fetchNextPage()}
              onRetry={() => userPostsRefetch()}
              isOwnProfile={isOwnProfile}
            />
          </View>
        )}

        {activeTab === "LABEL_VIDEOS" && (
          <View className="bg-chalk rounded-[16px] border border-ash-3">
            <ProfileVideoGrid
              videos={userVideos}
              isLoading={userPostsLoading}
              isError={userPostsError}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={() => fetchNextPage()}
              onRetry={() => userPostsRefetch()}
              isOwnProfile={isOwnProfile}
            />
          </View>
        )}

        {activeTab === "LABEL_FILES" && (
          <View className="bg-chalk rounded-[16px] border border-ash-3 p-4 gap-4">
            {/* Header and divider only once there is something to head. Photos
                and Videos show theirs inside their non-empty branch, so on an
                empty Files tab this was the odd one out — a title, a count of
                zero and a rule sitting above the empty message. */}
            {userDocuments.length > 0 && (
              <>
                <View className="flex-row justify-between items-center">
                  <View>
                    <Text className="text-[15px] font-bold text-onyx">
                      {t("LABEL_FILES")}
                    </Text>
                    <Text className="text-xs text-ash-1 mt-0.5">
                      {userDocuments.length}{" "}
                      {userDocuments.length === 1
                        ? t("LABEL_FILE")
                        : t("LABEL_FILES_COUNT")}
                    </Text>
                  </View>
                </View>

                <View className="h-px bg-ash-3" />
              </>
            )}

            {userPostsLoading && (
              <View className="items-center py-8">
                <ActivityIndicator size="small" />
              </View>
            )}

            {userPostsError && (
              <View className="items-center py-8 gap-3">
                <Text className="text-sm text-slate">
                  {t("ERROR_LOADING_POST")}
                </Text>
                <ScButton
                  mode="outlined"
                  text="BTN_RETRY"
                  onPress={() => userPostsRefetch()}
                />
              </View>
            )}

            {!userPostsLoading && !userPostsError && (
              <>
                {userDocuments.length ? (
                  <>
                    <AttachmentViewer
                      attachments={userDocuments}
                      previewHeight={120}
                    />
                    {hasNextPage && (
                      <Pressable
                        onPress={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        className="items-center py-2 hover:bg-ash-3 hover:rounded-lg"
                      >
                        {isFetchingNextPage ? (
                          <ActivityIndicator size="small" />
                        ) : (
                          <Text className="text-sm text-primary underline">
                            {t("LABEL_LOAD_MORE", {
                              defaultValue: "Load more",
                            })}
                          </Text>
                        )}
                      </Pressable>
                    )}
                  </>
                ) : (
                  // Same shape as the Photos and Videos empty states: icon,
                  // title, muted description, p-10.
                  <View className="flex-1 items-center justify-center gap-2 p-10">
                    <DocumentText1
                      size={40}
                      color={themeVars["--color-slate-default"] as string}
                    />
                    <Text className="text-base font-semibold text-onyx">
                      {t("LABEL_NO_FILES")}
                    </Text>
                    <Text className="text-sm text-slate text-center">
                      {t("LABEL_NO_FILES_DESC")}
                    </Text>
                  </View>
                )}
              </>
            )}
          </View>
        )}
      </ScrollView>

      {/* RIGHT COLUMN */}
      <View className="hidden md:flex md:w-[26%] flex-col gap-8 bg-chalk p-4 rounded-[16px] border border-ash-3 overflow-y-auto">
        <View className="flex-row justify-between items-center">
          <Text className="font-semibold text-sm lg:text-base text-onyx">
            {t("LABEL_PROFILE_INFORMATION")}
          </Text>
          {canAccessEdit && (
            <Pressable onPress={handleNavigateToEdit} hitSlop={8} className="p-1.5 rounded-full hover:bg-ash-3">
              <Edit size={18} color={themeVars["--color-ash-2"]} />
            </Pressable>
          )}
        </View>
        <ProfileInfoContent />
      </View>

      <ConfirmModal
        visible={confirmDeleteOpen}
        title="QUEST_CONFIRM_DELETE_POST"
        description="LABEL_THIS_ACTION_CANNOT_BE_UNDONE"
        confirmText="BTN_DELETE"
        cancelText="BTN_CANCEL"
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />


      <CreatePostModal
        visible={modalOpen}
        post={editPost ?? undefined}
        onClose={() => {
          setModalOpen(false);
          setEditPost(null);
        }}
        onSubmit={handleSubmit}
        isPosting={createPost.isPending}
      />
    </View>
  );
}
